import { useState } from "react";
import useSWR from "swr";
import { Box, Flex, Grid, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import baseService from "../../services/base";
import Loader from "../../components/Feedback/Loader";
import Narrative from "./Narrative";
import KpiCard from "./KpiCard";
import FilterBar, { filtersToQuery } from "./FilterBar";

// EG-38 — Dashboard "Operação/Workflow" (consultoria). Reusa GET /metrics/workflow.
// Tempo médio por etapa vem de certificate_status_history (LEAD entre transições).

const INK = "#292B30";
const INK_MUTED = "#5C6066";
const BRAND = "#0073BB";
const WARN = "#C05621";
const SURFACE = "#FFFFFF";

const fetcher = (url) => baseService.get(url).then((r) => r?.data);


// Duração específica e legível: dias inteiros (2 dias / 3 dias); abaixo de 1 dia,
// horas inteiras. Nada de fração de dia (ex.: "2.3 d").
function fmtDays(d) {
  if (d == null) return "—";
  const days = Math.round(d);
  if (days >= 1) return `${days} ${days === 1 ? "dia" : "dias"}`;
  const hours = Math.round(d * 24);
  if (hours >= 1) return `${hours} ${hours === 1 ? "hora" : "horas"}`;
  return "< 1 hora";
}

function CycleTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <Box bg={SURFACE} border="1px solid #E2E8F0" borderRadius="8px" p="10px" boxShadow="sm">
      <Text fontSize="13px" fontWeight={600} color={INK} textTransform="capitalize">
        {label}
      </Text>
      <Text fontSize="12px" color={INK_MUTED}>
        {fmtDays(row.avgDays)} em média · {row.count} medições
      </Text>
    </Box>
  );
}

export default function WorkflowDashboard() {
  const [filters, setFilters] = useState({});
  const { data, isLoading } = useSWR(
    `metrics/workflow${filtersToQuery(filters)}`,
    fetcher
  );

  return (
    <Box>
      <FilterBar
        value={filters}
        onChange={setFilters}
        show={["exercise", "certificate", "group", "period"]}
      />
      {isLoading ? (
        <Loader />
      ) : !data ? (
        <Flex bg={SURFACE} borderRadius="12px" p="48px" justify="center">
          <Text color={INK_MUTED}>Não foi possível carregar a operação.</Text>
        </Flex>
      ) : (
        <WorkflowContent data={data} />
      )}
    </Box>
  );
}

function WorkflowContent({ data }) {
  const stages = (data.cycleTimeByStatus || []).map((s) => ({
    status: s.status,
    avgDays: s.avgDays,
    count: s.count,
  }));
  const bottleneckStatus = data.bottleneck?.status;

  const rework = data.rework?.certsWithRework ?? 0;
  const narrativeParts = [];
  if (data.leadTime?.count) {
    narrativeParts.push(`O ciclo completo (início → auditado) leva ${fmtDays(data.leadTime.avgDays)} em média`);
  }
  if (data.bottleneck) {
    narrativeParts.push(`o gargalo está na etapa "${data.bottleneck.status}" (${fmtDays(data.bottleneck.avgDays)})`);
  }
  if (rework) {
    narrativeParts.push(`${rework} certificado(s) precisaram de retrabalho`);
  }
  const narrative = narrativeParts.length
    ? narrativeParts.join("; ").replace(/^./, (c) => c.toUpperCase()) + "."
    : "";

  return (
    <Box>
      {narrative && <Narrative tone={rework ? "warn" : "info"}>{narrative}</Narrative>}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="16px" mb="24px">
        <KpiCard
          label="Lead time médio"
          value={fmtDays(data.leadTime?.avgDays)}
          hint={`início → auditado · ${data.leadTime?.count ?? 0} certificados`}
          color={BRAND}
        />
        <KpiCard
          label="Gargalo"
          value={data.bottleneck ? fmtDays(data.bottleneck.avgDays) : "—"}
          hint={data.bottleneck ? data.bottleneck.status : "sem dados"}
          color={WARN}
        />
        <KpiCard
          label="Retrabalho"
          value={data.rework?.certsWithRework ?? 0}
          hint={`${data.rework?.transitions ?? 0} pedidos de adequação/ajuste`}
        />
      </SimpleGrid>

      <Box bg={SURFACE} borderRadius="12px" p={["20px", "28px"]}>
        <Heading size="sm" color={INK} mb="4px">
          Tempo médio por etapa
        </Heading>
        <Text fontSize="13px" color={INK_MUTED} mb="16px">
          Quanto tempo, em média, uma certificação fica parada em cada etapa (o
          gargalo aparece destacado).
        </Text>
        {stages.length > 0 ? (
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={stages}
              layout="vertical"
              margin={{ top: 8, right: 32, bottom: 8, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#EDF2F7" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 12, fill: INK_MUTED }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${Math.round(v)}`}
              />
              <YAxis
                type="category"
                dataKey="status"
                width={140}
                tick={{ fontSize: 12, fill: INK_MUTED }}
                tickLine={false}
                axisLine={{ stroke: "#E2E8F0" }}
              />
              <Tooltip content={<CycleTooltip />} cursor={{ fill: "rgba(0,115,187,0.06)" }} />
              <Bar dataKey="avgDays" radius={[0, 4, 4, 0]} maxBarSize={34}>
                <LabelList
                  dataKey="avgDays"
                  position="right"
                  fontSize={12}
                  fill={INK_MUTED}
                  formatter={(v) => fmtDays(v)}
                />
                {stages.map((r) => (
                  <Cell key={r.status} fill={r.status === bottleneckStatus ? WARN : BRAND} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Text color={INK_MUTED} fontSize="14px">
            Ainda não há transições de etapa registradas.
          </Text>
        )}
      </Box>
    </Box>
  );
}
